import * as Dialog from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import { when } from '@core'
import { Avatar } from '@features/Avatar'
import { timeAgo } from '@features/communityContent/timeAgo'
import { TOPICS, type CommunityTopic } from '@features/communityContent/topics'
import { useLocation, useNavigate } from '@solidjs/router'
import { userId } from '@stateApp'
import { useQuery } from '@tanstack/solid-query'
import dayjs from 'dayjs'
import { ArrowRight, CornerDownLeft, History, Search, X } from 'lucide-solid'
import { For, type JSX, Match, Show, Switch, createEffect, createMemo, createSignal, on, onCleanup, onMount } from 'solid-js'
import { Dynamic, Portal } from 'solid-js/web'
import { useService } from 'solid-services'
import { css, cx } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import { scrollbar } from 'styled-system/recipes'
import { MIN_QUERY_LENGTH, type SearchResult, globalSearch } from './api'
import { CATEGORIES, CATEGORY_MAP, type SearchCategory, categoryForPath } from './categories'
import { Highlight } from './Highlight'
import { countActive, toPayload } from './filters'
import { SearchFilterPanel, SearchFilterToggle } from './SearchFilters'
import { useGlobalSearch } from './state'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
export const shortcutLabel = isMac ? '⌘K' : 'Ctrl K'

const chip = (active: boolean) =>
  css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '1.5',
    flexShrink: 0,
    h: '7',
    px: '2.5',
    borderRadius: 'full',
    borderWidth: '1px',
    borderColor: active ? 'jade.8' : 'gray.7',
    bg: active ? 'jade.a3' : 'transparent',
    color: active ? 'fg.default' : 'fg.muted',
    fontSize: 'xs',
    fontWeight: 'medium',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
    _hover: { color: 'fg.default', borderColor: active ? 'jade.8' : 'gray.9' },
    _disabled: { opacity: 0.45, cursor: 'default' },
  })

const sectionLabel = css({
  fontSize: '11px',
  fontWeight: 'semibold',
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'fg.subtle',
})

const row = css({
  display: 'grid',
  gridTemplateColumns: '36px minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: '3',
  w: 'full',
  px: '3',
  py: '2',
  borderRadius: 'l2',
  textAlign: 'left',
  cursor: 'pointer',
  '&[data-active=true]': { bg: 'bg.subtle' },
})

/** The server stops counting at 100 matches per category */
const formatCount = (n: number) => (n >= 100 ? '100+' : String(n))

const prettify = (s?: unknown) => (typeof s === 'string' ? s.replaceAll('_', ' ').replaceAll('-', ' ') : '')

/** Secondary line under the title, specific to each kind of result */
const metaParts = (r: SearchResult): string[] => {
  const m = r.meta ?? {}
  switch (r.category) {
    case 'setup':
      return [
        m.symbol as string,
        m.exchange as string,
        prettify(m.position),
        prettify(m.stage),
        m.status === 'draft' ? 'Draft' : '',
      ]
    case 'chat':
      return [m.author ? `@${m.author}` : '', timeAgo(r.created_at)]
    case 'course':
      return m.kind === 'lesson' ? ['Lesson', r.subtitle] : ['Course']
    case 'event':
      return [
        m.recurring ? 'Recurring' : dayjs(m.starts_at as string).format('ddd, D MMM YYYY · HH:mm'),
        m.duration_mins ? `${m.duration_mins} min` : '',
      ]
    case 'recording':
      return [
        m.occurrence_date ? dayjs(m.occurrence_date as string).format('D MMM YYYY') : '',
        m.group as string,
        m.host ? `Hosted by ${m.host}` : '',
      ]
    case 'go_read':
    case 'go_watch':
      return [TOPICS[m.topic as CommunityTopic]?.label, m.author ? `@${m.author}` : '', timeAgo(r.created_at)]
    case 'member':
      return [prettify(m.role), m.is_top_gun ? 'Top Gun' : '']
    default:
      return []
  }
}

/** Categories where the text match is usually in the body, so the snippet is worth showing */
const showsSnippet = (r: SearchResult) =>
  r.subtitle && (r.category === 'chat' || r.category === 'go_read' || r.category === 'go_watch' ||
    r.category === 'setup' || r.category === 'event' || (r.category === 'course' && r.meta?.kind === 'course'))

function Leading(props: { result: SearchResult }) {
  const cfg = () => CATEGORY_MAP[props.result.category]
  return (
    <Switch
      fallback={
        <Flex
          w='36px'
          h='36px'
          borderRadius='l2'
          alignItems='center'
          justifyContent='center'
          style={{ background: `color-mix(in srgb, ${cfg().color} 14%, transparent)`, color: cfg().color }}
        >
          <Dynamic component={cfg().icon} size={18} />
        </Flex>
      }
    >
      <Match when={props.result.category === 'member'}>
        <Box w='36px' h='36px'>
          <Avatar id={props.result.id} />
        </Box>
      </Match>
      <Match when={props.result.category === 'go_watch' && props.result.meta?.thumbnail_url}>
        <Box
          w='36px'
          h='36px'
          borderRadius='l2'
          backgroundSize='cover'
          backgroundPosition='center'
          style={{ 'background-image': `url("${props.result.meta.thumbnail_url}")` }}
        />
      </Match>
    </Switch>
  )
}

function ResultRow(props: {
  result: SearchResult
  query: string
  index: number
  active: boolean
  showCategory: boolean
  onHover: (i: number) => void
  onSelect: (r: SearchResult) => void
}) {
  const cfg = () => CATEGORY_MAP[props.result.category]
  const meta = () => metaParts(props.result).filter(Boolean)

  return (
    <button
      type='button'
      id={`gs-item-${props.index}`}
      role='option'
      aria-selected={props.active}
      data-active={props.active}
      data-index={props.index}
      class={row}
      onMouseMove={() => props.onHover(props.index)}
      onClick={() => props.onSelect(props.result)}
    >
      <Leading result={props.result} />
      <Grid gap='0.5' minW='0'>
        <Flex gap='2' alignItems='center' minW='0'>
          <Show when={props.result.category === 'setup' && props.result.meta?.symbol && props.result.meta.symbol !== props.result.title}>
            <Text as='span' fontWeight='bold' fontSize='sm' flexShrink={0}>
              <Highlight text={props.result.meta.symbol as string} query={props.query} />
            </Text>
          </Show>
          <Text as='span' fontWeight='medium' fontSize='sm' truncate>
            <Highlight text={props.result.title} query={props.query} />
          </Text>
        </Flex>
        <Show when={showsSnippet(props.result)}>
          <Text as='span' fontSize='xs' color='fg.muted' lineClamp={2}>
            <Highlight text={props.result.subtitle} query={props.query} />
          </Text>
        </Show>
        <Show when={meta().length}>
          <Text as='span' fontSize='xs' color='fg.subtle' truncate textTransform='capitalize'>
            {meta().join(' · ')}
          </Text>
        </Show>
      </Grid>
      <Show
        when={props.showCategory}
        fallback={
          <Box color='fg.subtle' opacity={props.active ? 1 : 0} display={{ base: 'block', smDown: 'none' }}>
            <CornerDownLeft size={14} />
          </Box>
        }
      >
        <Text as='span' fontSize='11px' fontWeight='semibold' style={{ color: cfg().color }}>
          {props.result.category === 'course' && props.result.meta?.kind === 'lesson' ? 'Lesson' : cfg().singular}
        </Text>
      </Show>
    </button>
  )
}

export default function GlobalSearch() {
  const s = useService(useGlobalSearch)()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeIndex, setActiveIndex] = createSignal(0)
  let inputRef: HTMLInputElement | undefined
  let listRef: HTMLDivElement | undefined

  const isMobile = () => when.smDown()
  const hasQuery = () => s.query().length >= MIN_QUERY_LENGTH
  const currentSection = createMemo(() => categoryForPath(location.pathname))

  // Filters only apply while a category is selected
  const filterPayload = createMemo(() => (s.category() ? toPayload(s.categoryFilters()) : {}))
  const filtersVisible = () => !!s.category() && s.showFilters()

  // Desktop shows the panel beside the results as soon as a category is picked; on phones it
  // would cover the results, so there it waits for the Filters button
  createEffect(on(() => s.category(), (c) => c && s.setShowFilters(!isMobile()), { defer: true }))

  const results = useQuery(() => ({
    queryKey: ['global-search', userId(), s.query(), s.category() ?? 'all', filterPayload()],
    queryFn: () => globalSearch(s.query(), s.category(), filterPayload()),
    enabled: s.isOpen() && hasQuery(),
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  }))

  // Counts per category, taken from the "All" query so chips can show them even when scoped
  const allResults = useQuery(() => ({
    queryKey: ['global-search', userId(), s.query(), 'all'],
    queryFn: () => globalSearch(s.query()),
    enabled: s.isOpen() && hasQuery(),
    staleTime: 1000 * 30,
    placeholderData: (prev) => prev,
  }))

  const totals = createMemo(() => {
    const map = new Map<SearchCategory, number>()
    for (const r of allResults.data ?? []) map.set(r.category, Number(r.total))
    return map
  })

  // Groups in display order: the section you're in first, then the nav order
  const groups = createMemo(() => {
    const data = hasQuery() ? (results.data ?? []) : []
    const order = [...CATEGORIES].sort((a, b) =>
      a.key === currentSection() ? -1 : b.key === currentSection() ? 1 : 0,
    )
    return order
      .map((cfg) => ({ cfg, items: data.filter((r) => r.category === cfg.key) }))
      .filter((g) => g.items.length > 0)
  })

  const flat = createMemo(() => groups().flatMap((g) => g.items))

  // Index of each group's first row within the flat list, for keyboard navigation
  const startIndex = createMemo(() => {
    let index = 0
    const map = new Map<SearchCategory, number>()
    for (const g of groups()) {
      map.set(g.cfg.key, index)
      index += g.items.length
    }
    return map
  })

  createEffect(on([() => s.query(), () => s.category()], () => setActiveIndex(0)))

  // Keep the keyboard-selected row in view
  createEffect(() => {
    const el = listRef?.querySelector(`[data-index="${activeIndex()}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })

  // Close whenever the route changes (e.g. after picking a result)
  createEffect(on(() => location.pathname + location.search, () => s.close(), { defer: true }))

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        s.isOpen() ? s.close() : s.open()
        return
      }
      const target = e.target as HTMLElement
      const typing = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName)
      if (e.key === '/' && !typing && !s.isOpen()) {
        e.preventDefault()
        s.open()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  const select = (r: SearchResult) => {
    s.remember(s.input())
    s.close()
    navigate(r.link)
  }

  const seeAll = (category: SearchCategory) => {
    const cfg = CATEGORY_MAP[category]
    s.remember(s.input())
    if (cfg.seeAll) {
      s.close()
      navigate(cfg.seeAll(s.query()))
    } else {
      s.setCategory(category)
      inputRef?.focus()
    }
  }

  const onInputKeyDown = (e: KeyboardEvent) => {
    const count = flat().length
    if (e.key === 'ArrowDown' && count) {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % count)
    } else if (e.key === 'ArrowUp' && count) {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + count) % count)
    } else if (e.key === 'Enter') {
      const r = flat()[activeIndex()]
      if (r) {
        e.preventDefault()
        select(r)
      }
    } else if (e.key === 'Backspace' && !s.input() && s.category()) {
      // Backspace on an empty input drops the category scope, like removing a token
      s.setCategory(undefined)
    }
  }

  const placeholder = () =>
    s.category() ? `Search ${CATEGORY_MAP[s.category()!].label.toLowerCase()}…` : 'Search setups, chat, courses, people…'

  const Chips = () => (
    <Flex
      gap='1.5'
      px={{ base: '4', smDown: '3' }}
      pb='3'
      overflowX='auto'
      class={css({ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } })}
      role='tablist'
      aria-label='Filter by category'
    >
      <button type='button' role='tab' aria-selected={!s.category()} class={chip(!s.category())} onClick={() => s.setCategory(undefined)}>
        All
      </button>
      <For each={CATEGORIES}>
        {(cfg) => {
          const count = () => totals().get(cfg.key)
          const active = () => s.category() === cfg.key
          return (
            <button
              type='button'
              role='tab'
              aria-selected={active()}
              class={chip(active())}
              disabled={hasQuery() && !allResults.isFetching && !count() && !active()}
              onClick={() => {
                s.setCategory(active() ? undefined : cfg.key)
                inputRef?.focus()
              }}
            >
              <Dynamic component={cfg.icon} size={13} color={cfg.color} />
              {cfg.label}
              <Show when={hasQuery() && count()}>
                <Text as='span' color='fg.subtle' fontSize='11px'>
                  {formatCount(count()!)}
                </Text>
              </Show>
            </button>
          )
        }}
      </For>
    </Flex>
  )

  const EmptyState = () => (
    <Grid gap='5' px={{ base: '4', smDown: '3' }} py='4'>
      <Show when={s.recent().length}>
        <Grid gap='1'>
          <Flex justifyContent='space-between' alignItems='center' px='1'>
            <span class={sectionLabel}>Recent searches</span>
            <button type='button' class={css({ fontSize: 'xs', color: 'fg.subtle', cursor: 'pointer', _hover: { color: 'fg.default' } })} onClick={s.clearRecent}>
              Clear
            </button>
          </Flex>
          <For each={s.recent()}>
            {(term) => (
              <button
                type='button'
                class={cx(row, css({ gridTemplateColumns: '20px 1fr', py: '1.5', _hover: { bg: 'bg.subtle' } }))}
                onClick={() => {
                  s.setInput(term)
                  inputRef?.focus()
                }}
              >
                <History size={15} class={css({ color: 'fg.subtle' })} />
                <Text as='span' fontSize='sm'>{term}</Text>
              </button>
            )}
          </For>
        </Grid>
      </Show>
      <Grid gap='2'>
        <span class={sectionLabel}>Tips</span>
        <Text fontSize='sm' color='fg.muted'>
          Search setups by symbol, title, exchange or description; find chat messages, lessons, events, recordings,
          articles, videos and members. Pick a category above to narrow the results. Message text and descriptions
          are searched once you type 3 or more characters.
        </Text>
      </Grid>
    </Grid>
  )

  const Loading = () => (
    <Grid gap='3' px='4' py='4'>
      <For each={[0, 1, 2, 3]}>
        {() => (
          <Grid gridTemplateColumns='36px 1fr' gap='3' alignItems='center'>
            <Skeleton h='36px' w='36px' borderRadius='l2' />
            <Grid gap='1.5'>
              <Skeleton h='3' w='60%' />
              <Skeleton h='2.5' w='40%' />
            </Grid>
          </Grid>
        )}
      </For>
    </Grid>
  )

  const NoResults = () => (
    <Grid gap='3' justifyItems='center' textAlign='center' px='6' py='10'>
      <Search size={28} class={css({ color: 'fg.subtle' })} />
      <Text fontWeight='medium'>
        No {s.category() ? CATEGORY_MAP[s.category()!].label.toLowerCase() : 'results'} for “{s.query()}”
      </Text>
      <Switch fallback={<Text fontSize='sm' color='fg.muted'>Try a shorter or different keyword.</Text>}>
        <Match when={countActive(s.categoryFilters()) > 0}>
          <button type='button' class={chip(false)} onClick={s.clearFilters}>
            Clear filters
          </button>
        </Match>
        <Match when={s.category() && [...totals().values()].some(Boolean)}>
          <button type='button' class={chip(false)} onClick={() => s.setCategory(undefined)}>
            Search all categories instead
          </button>
        </Match>
      </Switch>
    </Grid>
  )

  const Results = () => {
    return (
      <Box px={{ base: '2', smDown: '1' }} pb='2' role='listbox' aria-label='Search results'>
        <For each={groups()}>
          {(group) => {
            // Scoped to a category, the rows carry the filtered total; "All" uses the unfiltered counts
            const total = () =>
              s.category() ? Number(group.items[0]?.total ?? group.items.length) : (totals().get(group.cfg.key) ?? group.items.length)
            const more = () => !s.category() && total() > group.items.length
            return (
              <Box pt='2'>
                <Flex
                  justifyContent='space-between'
                  alignItems='center'
                  px='3'
                  py='1.5'
                  position='sticky'
                  top='0'
                  zIndex='1'
                  bg='gray.surface.bg'
                >
                  <Flex alignItems='center' gap='1.5'>
                    <Dynamic component={group.cfg.icon} size={13} color={group.cfg.color} />
                    <span class={sectionLabel}>{group.cfg.label}</span>
                    <Text as='span' fontSize='11px' color='fg.subtle'>{formatCount(total())}</Text>
                  </Flex>
                  <Show when={more() || (s.category() && group.cfg.seeAll)}>
                    <button
                      type='button'
                      class={css({ display: 'inline-flex', alignItems: 'center', gap: '1', fontSize: 'xs', color: 'jade.11', cursor: 'pointer', _hover: { textDecoration: 'underline' } })}
                      onClick={() => seeAll(group.cfg.key)}
                    >
                      {group.cfg.seeAll ? `Open in ${group.cfg.label}` : `See all ${formatCount(total())}`}
                      <ArrowRight size={12} />
                    </button>
                  </Show>
                </Flex>
                <For each={group.items}>
                  {(r, i) => {
                    const idx = () => (startIndex().get(group.cfg.key) ?? 0) + i()
                    return (
                      <ResultRow
                        result={r}
                        query={s.query()}
                        index={idx()}
                        active={activeIndex() === idx()}
                        showCategory={false}
                        onHover={setActiveIndex}
                        onSelect={select}
                      />
                    )
                  }}
                </For>
              </Box>
            )
          }}
        </For>
      </Box>
    )
  }

  const Footer = (): JSX.Element => (
    <Flex
      display={{ base: 'flex', smDown: 'none' }}
      gap='4'
      px='4'
      py='2.5'
      borderTopWidth='1px'
      borderColor='border'
      fontSize='xs'
      color='fg.subtle'
      alignItems='center'
    >
      <Flex gap='1' alignItems='center'><Kbd size='sm'>↑</Kbd><Kbd size='sm'>↓</Kbd> navigate</Flex>
      <Flex gap='1' alignItems='center'><Kbd size='sm'>↵</Kbd> open</Flex>
      <Flex gap='1' alignItems='center'><Kbd size='sm'>esc</Kbd> close</Flex>
    </Flex>
  )

  return (
    <Dialog.Root
      open={s.isOpen()}
      onOpenChange={({ open }) => s.setIsOpen(open)}
      initialFocusEl={() => inputRef ?? null}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner
          alignItems={{ base: 'flex-start', smDown: 'stretch' }}
          pt={{ base: '6vh', smDown: '0' }}
          px={{ base: '4', smDown: '0' }}
        >
          <Dialog.Content
            aria-label='Search'
            p='0'
            gap='0'
            my='0'
            overflow='hidden'
            maxW={{ base: filtersVisible() ? '1080px' : '780px', smDown: '100%' }}
            transition='max-width 0.2s ease'
            // Fixed height so the window doesn't jump around while results load or filters change
            maxH={{ base: '84vh', smDown: '100dvh' }}
            h={{ base: 'min(84vh, 820px)', smDown: '100dvh' }}
            borderRadius={{ base: 'l3', smDown: '0' }}
            pt={{ base: '0', smDown: 'var(--safe-area-inset-top)' }}
            pb={{ base: '0', smDown: 'var(--safe-area-inset-bottom)' }}
          >
            <Flex alignItems='center' gap='2' px={{ base: '4', smDown: '3' }} py='3'>
              <Show when={results.isFetching} fallback={<Search size={18} class={css({ color: 'fg.subtle', flexShrink: 0 })} />}>
                <Spinner size='sm' />
              </Show>
              <input
                ref={inputRef}
                value={s.input()}
                onInput={(e) => s.setInput(e.currentTarget.value)}
                onKeyDown={onInputKeyDown}
                placeholder={placeholder()}
                aria-label='Search'
                aria-activedescendant={flat().length ? `gs-item-${activeIndex()}` : undefined}
                autocomplete='off'
                spellcheck={false}
                enterkeyhint='search'
                type='search'
                class={css({
                  flex: '1',
                  minW: '0',
                  h: '9',
                  bg: 'transparent',
                  outline: 'none',
                  // 16px stops iOS zooming into the field
                  fontSize: { base: 'md', smDown: '16px' },
                  color: 'fg.default',
                  _placeholder: { color: 'fg.subtle' },
                  '&::-webkit-search-cancel-button': { display: 'none' },
                })}
              />
              <Show when={s.input()}>
                <button
                  type='button'
                  aria-label='Clear search'
                  class={css({ color: 'fg.subtle', cursor: 'pointer', p: '1', _hover: { color: 'fg.default' } })}
                  onClick={() => {
                    s.setInput('')
                    inputRef?.focus()
                  }}
                >
                  <X size={16} />
                </button>
              </Show>
              <Show when={s.category()}>
                <SearchFilterToggle />
              </Show>
              <Show
                when={isMobile()}
                fallback={
                  <Kbd size='sm' class={css({ cursor: 'pointer' })} onClick={s.close}>esc</Kbd>
                }
              >
                <button type='button' class={css({ fontSize: 'sm', color: 'jade.11', fontWeight: 'medium', pl: '1', cursor: 'pointer' })} onClick={s.close}>
                  Cancel
                </button>
              </Show>
            </Flex>

            <Chips />

            <Grid
              flex='1'
              minH='0'
              gap='0'
              borderTopWidth='1px'
              borderColor='border'
              gridTemplateColumns={{ base: filtersVisible() ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)', smDown: 'minmax(0, 1fr)' }}
            >
            <Box
              ref={listRef}
              minH='0'
              overflowY='auto'
              class={scrollbar()}
              // On phones the filter panel takes the results' place until "Show results"
              display={{ base: 'block', smDown: filtersVisible() ? 'none' : 'block' }}
            >
              <Switch>
                <Match when={!hasQuery()}>
                  <EmptyState />
                </Match>
                <Match when={results.isPending}>
                  <Loading />
                </Match>
                <Match when={results.isError}>
                  <Text color='error.10' p='6' textAlign='center'>Search failed. Please try again.</Text>
                </Match>
                <Match when={flat().length === 0 && !results.isFetching}>
                  <NoResults />
                </Match>
                <Match when={flat().length > 0}>
                  <Results />
                </Match>
              </Switch>
            </Box>
            <Show when={filtersVisible()}>
              <Box minH='0' borderLeftWidth={{ base: '1px', smDown: '0' }} borderColor='border'>
                <SearchFilterPanel
                  onDone={isMobile() ? () => s.setShowFilters(false) : undefined}
                  resultCount={hasQuery() && !results.isFetching ? Number(flat()[0]?.total ?? 0) : undefined}
                />
              </Box>
            </Show>
            </Grid>

            <Footer />
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
